// Motorist help guides — single source of truth for `/guide/$slug` pages.
// Keep tone practical, honest, UK English. No fake claims, no guarantees.

export interface GuideSection {
  heading: string;
  /** Plain-text paragraphs. Use the bullets field for lists. */
  body?: string[];
  bullets?: string[];
}

export interface GuideFAQ { q: string; a: string }

export interface Guide {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  excerpt: string;
  /** ISO date used for BlogPosting datePublished. */
  publishedAt: string;
  sections: GuideSection[];
  faqs: GuideFAQ[];
  /** Related service pages to surface as CTAs. */
  relatedServices: { label: string; to: string }[];
  /** Other guides to cross-link (slugs). */
  relatedGuides: string[];
  readingMinutes: number;
}

export const GUIDES: Guide[] = [
  {
    slug: "mot-warning-lights-before-test",
    title: "Dashboard Warning Lights Before MOT: What Drivers Should Know",
    metaTitle: "Dashboard Warning Lights Before MOT | Woodseer Autos East London",
    metaDescription:
      "Which dashboard warning lights cause MOT failure and what to do before your test. Honest advice from a DVSA-authorised garage in Poplar, East London.",
    category: "Warning lights & diagnostics",
    excerpt:
      "A warning light on the dash is the single most common reason cars roll into our workshop the week before an MOT. Here's what each one means and what's worth doing first.",
    publishedAt: "2026-05-27",
    readingMinutes: 6,
    sections: [
      {
        heading: "Which warning lights matter for the MOT",
        body: [
          "Since the 2018 MOT rules tightened, several dashboard warning lights are an automatic fail — even if the car otherwise drives perfectly. The tester doesn't need to prove the underlying fault; the illuminated lamp is the defect.",
        ],
        bullets: [
          "Engine management (EML / check-engine)",
          "ABS",
          "Airbag / SRS",
          "Electronic stability control (ESC / ESP)",
          "Brake system warning",
          "Power steering (EPAS) on most cars after 2012",
          "Tyre pressure monitoring (TPMS) on most cars after 2012",
          "Emissions / particulate filter (DPF) warning on diesels",
        ],
      },
      {
        heading: "Solid vs flashing lights",
        body: [
          "A solid amber light usually means 'investigate soon'. A flashing light — especially the engine-management lamp — means stop driving and call a garage. Flashing EML on a petrol car typically signals misfire that can wreck the catalytic converter within minutes.",
        ],
      },
      {
        heading: "What to do before booking the MOT",
        bullets: [
          "Note which lights are on, when they came on and whether the car behaves differently",
          "Check the dipped headlights, indicators and brake lights work — bulb failures often trigger a CAN bus warning",
          "Top up screenwash, brake fluid and coolant to the MAX line — low-level warnings will cause a test fail",
          "If the EML is on, get a diagnostic scan before the test rather than after a fail — it's cheaper and faster",
        ],
      },
      {
        heading: "When to call a garage",
        body: [
          "Any red warning light, any flashing light, any combination of ABS + brake light, or a warning that returns after you reset it — book a diagnostic. We charge £45 for a full scan and the fee comes off any repair work we do.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will a single airbag warning fail my MOT?",
        a: "Yes. Since May 2018 the airbag (SRS) warning light is a major defect — automatic fail regardless of whether the airbag itself is working.",
      },
      {
        q: "Can you clear warning lights so my car passes?",
        a: "We don't 'clear and hope'. If a light is on there's a fault stored. We scan it, tell you the cause and the cost to fix it properly. Anything else is dangerous and quickly comes back.",
      },
      {
        q: "My TPMS light is on — is that really a fail?",
        a: "On cars first registered from 1 January 2012 onwards, yes. We can usually reset it after pressure correction; if a sensor has failed we'll quote before replacing.",
      },
      {
        q: "What's the difference between EML on and EML flashing?",
        a: "Solid means a fault is stored but the engine can still run safely for a short trip. Flashing means active misfire — keep driving and you can melt the cat. Pull over and call us.",
      },
    ],
    relatedServices: [
      { label: "Engine Diagnostics", to: "/diagnostics" },
      { label: "MOT Testing", to: "/mot" },
      { label: "Mechanical Repairs", to: "/mechanical-repairs" },
    ],
    relatedGuides: ["engine-light-east-london", "brake-noise-squeaking-grinding"],
  },
  {
    slug: "brake-noise-squeaking-grinding",
    title: "Squeaking or Grinding Brakes: What It Usually Means",
    metaTitle: "Squeaking or Grinding Brakes — What It Means | Woodseer Autos",
    metaDescription:
      "Brakes squeaking or grinding? Honest guide from East London mechanics covering causes, safety, MOT impact and when to book a brake inspection.",
    category: "Brakes, tyres & suspension",
    excerpt:
      "Brake noise rarely means 'nothing'. Here's how to tell the difference between cold-morning squeal and a real safety problem.",
    publishedAt: "2026-05-27",
    readingMinutes: 5,
    sections: [
      {
        heading: "Squeaking vs grinding — what each sound usually means",
        body: [
          "Squeak: high-pitched, comes and goes, often disappears once the brakes are warm. Usually surface glazing, brake-dust contamination or the wear indicator just starting to touch the disc — pads are near the end of life but not yet dangerous.",
          "Grind: low, metallic, gets worse the harder you press. The friction material is gone and the steel backing plate is now cutting into the disc. This is unsafe and usually means pads AND discs need replacing.",
        ],
      },
      {
        heading: "Common underlying causes",
        bullets: [
          "Worn brake pads (most common)",
          "Glazed discs from light city use or stuck calipers",
          "Sticky / seized caliper sliders pulling the car to one side",
          "Corroded discs after the car has stood unused for weeks",
          "Wrong-spec or cheap aftermarket pads fitted at the last service",
        ],
      },
      {
        heading: "When it becomes unsafe",
        body: [
          "If you can feel pulsing through the pedal, smell hot metal after a stop, or hear continuous grinding even at low speed — stop driving and book it in. Brake failure under load is sudden, not gradual.",
        ],
      },
      {
        heading: "Why brake issues fail the MOT",
        body: [
          "The tester measures brake imbalance, efficiency and disc condition on a rolling brake-test machine. Pads below 1.5mm, discs more than 25% worn, contaminated friction surfaces or seized calipers are all automatic fails.",
        ],
      },
    ],
    faqs: [
      {
        q: "My brakes only squeak first thing in the morning — is that bad?",
        a: "Usually no. Overnight moisture leaves a thin film of rust on the disc surface; the first few stops scrub it off and the noise vanishes. If it carries on once the brakes are warm, book a check.",
      },
      {
        q: "How long do brake pads usually last?",
        a: "On East London stop-start driving, front pads typically last 18,000–25,000 miles, rears 35,000–45,000. Heavy city use and lots of short trips shorten that.",
      },
      {
        q: "Can I just replace the pads and leave the discs?",
        a: "Only if the discs are within spec and not heavily lipped. We measure thickness and runout before quoting — fitting new pads onto worn discs ruins them within weeks.",
      },
      {
        q: "How much does a brake job cost at Woodseer?",
        a: "See our cost guide at /cost/brake-pads-discs-east-london for current pricing. We quote before any work and only fit reputable brand pads.",
      },
    ],
    relatedServices: [
      { label: "Brakes (Poplar)", to: "/poplar/brakes" },
      { label: "Mechanical Repairs", to: "/mechanical-repairs" },
      { label: "MOT Testing", to: "/mot" },
    ],
    relatedGuides: ["car-pulling-to-one-side", "mot-warning-lights-before-test"],
  },
  {
    slug: "tyre-check-before-mot",
    title: "Tyre Checks Before MOT: Tread, Pressure, Damage & Wear",
    metaTitle: "Tyre Checks Before MOT | Tread, Pressure & Damage | Woodseer Autos",
    metaDescription:
      "Free DIY tyre check before your MOT. Tread depth, sidewall damage, pressure, uneven wear — what fails and what to fix at a Poplar garage.",
    category: "Brakes, tyres & suspension",
    excerpt:
      "Tyres are the single biggest 'fixable in 10 minutes' MOT failure we see. Run through this before booking your test.",
    publishedAt: "2026-05-27",
    readingMinutes: 5,
    sections: [
      {
        heading: "Legal tread depth — the 20p test",
        body: [
          "UK law requires at least 1.6mm of tread across the central three-quarters of the tyre, all the way round. Slot a 20p coin into the main grooves — if you can see the outer band of the coin, the tyre is at or below the limit.",
          "We replace at around 3mm. Below that, wet braking distance more than doubles compared to a fresh tyre — and the MOT doesn't care that 'it was fine last week'.",
        ],
      },
      {
        heading: "Uneven wear — what it tells you",
        bullets: [
          "Inner edge worn: tracking out, worn lower arm bushes or tired anti-roll links",
          "Outer edge worn: under-inflation or hard cornering",
          "Centre strip worn: over-inflation",
          "Feathered / cupped wear: failed shock absorber on that corner",
        ],
        body: [
          "Replacing tyres without fixing the cause means burning through the new ones in months. We check tracking and suspension as part of any tyre fit.",
        ],
      },
      {
        heading: "Sidewall damage and lumps",
        body: [
          "Any cut, bulge or visible cord on the sidewall is an automatic MOT fail and means the tyre needs replacing — not repairing. Pothole damage and kerb impacts are the usual culprits in Poplar and on the Isle of Dogs.",
        ],
      },
      {
        heading: "Pressures — quick check",
        body: [
          "Correct pressures are on a sticker inside the driver's door shut or in the fuel-filler flap. Check cold, ideally before a long drive. Underinflation kills MPG and overheats tyres; overinflation reduces grip and wears the centre strip.",
        ],
      },
    ],
    faqs: [
      {
        q: "Will a slow puncture fail my MOT?",
        a: "Not in itself — but if the tyre is below pressure on the day of the test, or the cause is sidewall damage, that's a fail. Get any slow puncture investigated before booking.",
      },
      {
        q: "Do all four tyres have to match?",
        a: "They must match in size and load/speed rating across an axle. Mixing radial and cross-ply, or summer and winter, across an axle is a fail.",
      },
      {
        q: "Can you repair a puncture or do I need a new tyre?",
        a: "If the puncture is in the central tread area, under 6mm, and the sidewall is undamaged, we can plug-and-patch to BS AU 159 standard. Otherwise it's a replacement.",
      },
      {
        q: "How much is a new tyre fitted?",
        a: "Depends on size and brand — we stock budget, mid-range and premium. Call with your size (sidewall reads e.g. 205/55R16) and we'll quote on the spot.",
      },
    ],
    relatedServices: [
      { label: "Tyres (Poplar)", to: "/poplar/tyres" },
      { label: "MOT Testing", to: "/mot" },
      { label: "Servicing", to: "/servicing" },
    ],
    relatedGuides: ["mot-warning-lights-before-test", "car-pulling-to-one-side"],
  },
  {
    slug: "car-pulling-to-one-side",
    title: "Car Pulling to One Side: Tracking, Brakes, Tyres or Suspension?",
    metaTitle: "Car Pulling to One Side — Causes | Woodseer Autos East London",
    metaDescription:
      "Why does your car pull left or right? Tracking, brakes, tyre pressure or suspension — diagnose the common causes with help from a Poplar garage.",
    category: "Brakes, tyres & suspension",
    excerpt:
      "A car that pulls under braking and a car that pulls when you let go of the wheel are two different problems. Here's how to tell them apart.",
    publishedAt: "2026-05-27",
    readingMinutes: 5,
    sections: [
      {
        heading: "Pulls only when braking",
        body: [
          "Almost always a brake-side issue: a sticking caliper slider, a partially seized piston, a contaminated pad, or one disc heavily corroded after the car stood unused. Sometimes a worn flexible brake hose ballooning on one side.",
        ],
      },
      {
        heading: "Pulls when you let go of the steering wheel",
        body: [
          "Usually tracking (wheel alignment) — common after a hard pothole hit or a kerb strike. Other suspects: uneven tyre pressures, mismatched tyres across the front axle, or a worn lower-arm bush.",
        ],
      },
      {
        heading: "Pulls only over bumps",
        body: [
          "Suspension. Worn top mounts, broken coil spring or a tired shock absorber on one corner. You'll often hear a clonk over speed bumps as well.",
        ],
      },
      {
        heading: "Safety risks and MOT implications",
        body: [
          "Brake imbalance over 25% is an MOT fail. Significant tracking issues won't fail on their own but the tyre wear they cause will. Worn suspension components are a fail.",
          "If the pull is strong enough that you have to actively counter-steer at 30mph, stop driving and book it in.",
        ],
      },
    ],
    faqs: [
      {
        q: "How much is a wheel alignment / tracking check?",
        a: "We use 4-wheel laser alignment and adjust the front toe — call for current pricing. Always cheaper than a new set of front tyres.",
      },
      {
        q: "Can I drive a car that pulls under braking?",
        a: "Short distance to a garage, yes — driven gently. Anything more risks ruining the disc on the dragging side and pulling the car into the next lane in an emergency stop.",
      },
      {
        q: "Will a pothole knock my tracking out?",
        a: "Frequently, yes. East London potholes are notorious. If the wheel sits at a noticeable angle after a hit, get it checked the same week.",
      },
    ],
    relatedServices: [
      { label: "Suspension Repairs (Poplar)", to: "/poplar/suspension-repairs" },
      { label: "Mechanical Repairs", to: "/mechanical-repairs" },
      { label: "MOT Testing", to: "/mot" },
    ],
    relatedGuides: ["brake-noise-squeaking-grinding", "tyre-check-before-mot"],
  },
  {
    slug: "engine-light-east-london",
    title: "Engine Warning Light On? What to Do Before It Gets Worse",
    metaTitle: "Engine Warning Light On — What to Do | Woodseer Autos East London",
    metaDescription:
      "Engine management light on? Honest advice from East London mechanics — what it means, why guessing is expensive, and how a diagnostic scan helps.",
    category: "Warning lights & diagnostics",
    excerpt:
      "The check-engine light covers hundreds of possible faults. Here's how to triage it before it becomes a tow-truck job.",
    publishedAt: "2026-05-27",
    readingMinutes: 5,
    sections: [
      {
        heading: "Solid vs flashing — read this first",
        body: [
          "Solid amber: a fault is stored but the engine isn't in immediate danger. Drive gently to a garage, don't ignore it.",
          "Flashing: active misfire. Unburnt fuel is being dumped into the catalytic converter and can melt it within minutes. Pull over, let the engine cool, and call a recovery service or us.",
        ],
      },
      {
        heading: "Common causes — in rough order of how often we see them",
        bullets: [
          "Faulty ignition coil or spark plug (misfire)",
          "Loose or failed fuel filler cap (evap leak)",
          "O2 / lambda sensor failure",
          "MAF or MAP sensor contamination",
          "EGR valve sticking on diesels",
          "DPF approaching blockage on diesels with lots of short trips",
          "Catalytic converter efficiency dropping with age",
        ],
      },
      {
        heading: "Why 'just changing the part' is expensive",
        body: [
          "Two cars with identical fault codes can need completely different repairs. The code points at a circuit, not always the failed component. Throwing a £180 sensor at the problem when the actual fault is a £6 vacuum hose is the most common DIY mistake we fix.",
        ],
      },
      {
        heading: "What a proper diagnostic scan covers",
        bullets: [
          "All ECUs, not just the engine — gearbox, ABS, airbag, body control",
          "Live data: fuel trims, sensor voltages, mixture readings",
          "Freeze-frame conditions when the fault was logged",
          "Bidirectional tests where the tool supports it",
        ],
        body: [
          "Our diagnostic is £45 and the fee comes off any repair we do.",
        ],
      },
    ],
    faqs: [
      {
        q: "Can I drive with the engine light on?",
        a: "If it's solid amber and the engine feels normal, you can drive cautiously to a garage. If it's flashing, or the car is shaking, losing power or smoking — stop.",
      },
      {
        q: "Will Halfords / Kwik Fit reset the light for free?",
        a: "Often yes, but resetting only clears the lamp. The fault is still stored and will return — usually within a few drive cycles. Better to find the cause.",
      },
      {
        q: "How quickly can you scan my car?",
        a: "Most days we can fit a same-day diagnostic. Call ahead so we can hold a bay for you.",
      },
      {
        q: "Is engine light an MOT fail?",
        a: "Yes — the engine management lamp is an automatic major defect at MOT since May 2018.",
      },
    ],
    relatedServices: [
      { label: "Engine Diagnostics", to: "/diagnostics" },
      { label: "Cost: Diagnostics", to: "/cost/diagnostics-east-london" },
      { label: "Mechanical Repairs", to: "/mechanical-repairs" },
    ],
    relatedGuides: ["mot-warning-lights-before-test", "car-pulling-to-one-side"],
  },
  {
    slug: "selling-car-repair-checklist",
    title: "Selling Your Car? Repairs Worth Checking Before Listing",
    metaTitle: "Selling Your Car — Pre-Sale Repair Checklist | Woodseer Autos",
    metaDescription:
      "What's worth fixing before selling your car in East London? Honest pre-sale checklist from an independent garage — MOT, bodywork, tyres and service history.",
    category: "Buying & selling a used car",
    excerpt:
      "Some pre-sale repairs add real money to the asking price. Others are wasted spend. Here's how we'd advise prepping a car for sale.",
    publishedAt: "2026-05-27",
    readingMinutes: 6,
    sections: [
      {
        heading: "Always worth doing",
        bullets: [
          "Fresh MOT — a long-ticket car always sells faster and for more",
          "Sort any dashboard warning lights — buyers walk away from anything glowing on the cluster",
          "Replace tyres at or below 3mm — viewers always check tread",
          "Replace any failed bulb (headlight, brake, indicator)",
          "Top up screenwash and oil — empty bottles look like neglect",
        ],
      },
      {
        heading: "Worth doing if the car is otherwise tidy",
        bullets: [
          "Minor bodywork: kerbed alloy, small dent, scuffed bumper. Cosmetic flaws kill perceived value faster than mechanical ones",
          "Cambelt if it's due in the next 10,000 miles — buyers price the job in at retail",
          "A full service with paperwork — adds confidence and asking-price headroom",
        ],
      },
      {
        heading: "Probably not worth it",
        bullets: [
          "Major engine or gearbox work on a car worth under £2,500 — sell as-is and price honestly",
          "Brand-new tyres if the existing ones still have 4mm",
          "Detailing on a car the trade will buy regardless",
        ],
      },
      {
        heading: "Paperwork buyers actually look at",
        bullets: [
          "MOT history (check on gov.uk before you list)",
          "Service stamps or invoices",
          "V5C in your name",
          "Locking wheel-nut key",
          "Spare keys (massive value-add)",
        ],
      },
      {
        heading: "Photos that sell the car",
        body: [
          "Clean it inside and out, take photos in soft daylight (not midday sun), include all four sides, the boot, the dash with engine running showing no warning lights, and a clear shot of the odometer.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is it worth getting a full service before selling?",
        a: "If the car is priced over £3,000 — usually yes. A full service with a printed invoice typically returns more than it costs in asking-price headroom and reduces haggling.",
      },
      {
        q: "Should I fix a kerbed alloy before selling?",
        a: "Almost always yes. Alloy refurb is cheap (£60–£90 per wheel) and removes a buyer negotiating point worth several hundred pounds.",
      },
      {
        q: "Do I need an MOT to sell my car?",
        a: "No, but cars sold without one go to trade prices, not private prices. A fresh MOT typically adds £300–£800 to the achievable price.",
      },
    ],
    relatedServices: [
      { label: "Bodywork & Paint", to: "/bodywork" },
      { label: "Servicing", to: "/servicing" },
      { label: "MOT Testing", to: "/mot" },
    ],
    relatedGuides: ["mot-warning-lights-before-test", "tyre-check-before-mot"],
  },
];

export function getGuide(slug: string): Guide | undefined {
  return GUIDES.find((g) => g.slug === slug);
}

export function getRelatedGuides(slug: string): Guide[] {
  const g = getGuide(slug);
  if (!g) return [];
  return g.relatedGuides.map((s) => getGuide(s)).filter((x): x is Guide => !!x);
}
